import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {
  ChatSliceInitalState,
  GetChatMessagesRequestPayload,
  GetChatMessagesSuccessResponse,
  GetChatMessagesFailureResponse,
  SendChatMessageRequestPayload,
  SendChatMessageSuccessResponse,
  SendChatMessageFailureResponse,
  ChatMessage,
} from '@/interface';
import {ChatMessageStatus} from '@/constants';

const initialState: ChatSliceInitalState = {
  chatSuccess: false,
  chatFailure: false,
  chatData: {},
};

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    /**
     * get chat messages request
     */
    getChatMessagesRequest: (state, action: PayloadAction<GetChatMessagesRequestPayload>) => {
      state.chatSuccess = false;
      state.chatFailure = false;
      state.chatData[action.payload.id] = {
        messages: [],
        isLoading: true,
      };
    },
    getChatMessagesSuccess: (state, action: PayloadAction<GetChatMessagesSuccessResponse>) => {
      state.chatSuccess = action.payload.status_code;
      state.chatData[action.payload.data.consultation_id] = {
        messages: action.payload.data.messages,
        isLoading: false,
      };
    },
    getChatMessagesFailure: (state, action: PayloadAction<GetChatMessagesFailureResponse>) => {
      state.chatFailure = action.payload.status_code;

      if (action.payload.status_code === 'E-10123') {
        state.chatData[action.payload.data.consultation_id].messages = [];
      }
      state.chatData[action.payload.data.consultation_id].isLoading = false;
    },

    /**
     * send chat message request
     */
    sendChatMessageRequest: (state, action: PayloadAction<SendChatMessageRequestPayload>) => {
      state.chatSuccess = false;
      state.chatFailure = false;

      if (!state.chatData[action.payload.id]) {
        state.chatData[action.payload.id] = {
          messages: [],
          isLoading: false,
        };
      }
    },
    sendChatMessageSuccess: (state, action: PayloadAction<SendChatMessageSuccessResponse>) => {
      state.chatSuccess = action.payload.status_code;
      state.chatData[action.payload.data.consultation_id] = {
        messages: [...state.chatData[action.payload.data.consultation_id].messages, {...action.payload.data.message}],
        isLoading: false,
      };
    },
    sendChatMessageFailure: (state, action: PayloadAction<SendChatMessageFailureResponse>) => {
      state.chatFailure = action.payload.status_code;
      state.chatData[action.payload.data.consultation_id].isLoading = false;
    },

    scheduleMeetRequest: (state, action: PayloadAction<{id: number; meet_time: string; provider?: number; onSuccess?: () => void; onError?: () => void}>) => {
      state.chatSuccess = false;
      state.chatFailure = false;
    },
    scheduleMeetSuccess: (state, action: PayloadAction<any>) => {
      state.chatSuccess = action.payload.status_code;
    },
    scheduleMeetFailure: (state, action: PayloadAction<any>) => {
      state.chatFailure = action.payload.status_code;
    },

    // cancel & reset chat messages
    cancelChatMessagesRequest: state => {
      for (const key in state.chatData) {
        state.chatData[key].isLoading = false;
      }
    },

    resetChatMessagesRequest: state => {
      state.chatSuccess = false;
      state.chatFailure = false;
    },

    /**
     * utilty
     */
    addChatMessage(state, action: PayloadAction<{consultation_case_id: number; message: ChatMessage}>) {
      const chat_id = action.payload.consultation_case_id;
      // initate message if not exists
      if (!state.chatData[chat_id]) {
        state.chatData[chat_id] = {
          isLoading: false,
          messages: [],
        };
      }

      // do not add if message already exists
      const alreadyExists = state.chatData[chat_id].messages.some(m => m.id === action.payload.message.id);
      if (alreadyExists) return;

      // add message
      state.chatData[chat_id].messages = [...state.chatData[chat_id].messages, action.payload.message];
    },

    markChatMessagesAsDelivered(state, action: PayloadAction<{consultation_id: number; message_id: number}>) {
      if (!state.chatData[action.payload.consultation_id]) return;
      state.chatData[action.payload.consultation_id].messages = state.chatData[action.payload.consultation_id].messages.map(item => {
        if (item.id === action.payload.message_id) {
          return {
            ...item,
            status: ChatMessageStatus.Delivered,
          };
        }
        return item;
      });
    },
  },
});

export const {
  // get chat messages
  getChatMessagesRequest,
  getChatMessagesSuccess,
  getChatMessagesFailure,

  // send chat message
  sendChatMessageRequest,
  sendChatMessageSuccess,
  sendChatMessageFailure,

  // schedule meet
  scheduleMeetRequest,
  scheduleMeetSuccess,
  scheduleMeetFailure,

  // cancel & reset chat messages
  cancelChatMessagesRequest,
  resetChatMessagesRequest,

  // utility
  addChatMessage,
  markChatMessagesAsDelivered,
} = chatSlice.actions;
export default chatSlice.reducer;
